/**
 * AI Streaming Service
 * Handles streaming AI responses with ReadableStream processing
 * Based on research patterns from planning phase
 */

import {
  AIPreviewRequest,
  AIPreviewResponse,
  AIPreviewError,
  AIStreamingConfig,
  AIPreviewState,
  AICitation,
} from '../pages/search/components/ai-preview/types';

export class AIStreamingService {
  private config: AIStreamingConfig;
  private currentController: AbortController | null = null;
  private citations: AICitation[] = [];

  constructor(config: AIStreamingConfig) {
    this.config = config;
  }

  async streamAIResponse(
    request: AIPreviewRequest,
    onChunk: (chunk: string, isComplete: boolean) => void,
    onError: (error: AIPreviewError) => void
  ): Promise<AIPreviewResponse> {
    this.currentController = new AbortController();
    this.citations = [];

    try {
      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...this.config.headers,
        },
        body: JSON.stringify({
          prompt: request.prompt,
          session_id: request.session_id,
          // Only sent when set — older API builds ignore unknown fields, and
          // omitting it gets the API's conversational default either way.
          ...(request.mode ? { mode: request.mode } : {}),
        }),
        signal: this.currentController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await this.processStream(response, onChunk, onError);
    } catch (error) {
      const aiError = this.createError(error);
      onError(aiError);
      throw aiError;
    }
  }

  private async processStream(
    response: Response,
    onChunk: (chunk: string, isComplete: boolean) => void,
    onError: (error: AIPreviewError) => void
  ): Promise<AIPreviewResponse> {
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let streamedText = '';
    let fullContent = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.trim() === '') continue;

          // Completion sentinel: the backend sends `data: [DONE]` (a bare
          // `[DONE]` is also accepted for older fixtures).
          const donePayload = line.startsWith('data: ')
            ? line.substring(6).trim()
            : line.trim();
          if (donePayload === '[DONE]') {
            onChunk(streamedText, true);
            return this.createResponse(fullContent, streamedText, true);
          }

          const processedContent = this.processStreamLine(line);
          if (processedContent) {
            streamedText += processedContent;
            fullContent += processedContent;

            // Throttle updates with 50ms delay for smooth visual streaming
            await new Promise((resolve) => setTimeout(resolve, 50));
            onChunk(streamedText, false);
          }
        }
      }
    } catch (error) {
      const aiError = this.createError(error);
      onError(aiError);
      throw aiError;
    } finally {
      reader.releaseLock();
    }

    return this.createResponse(fullContent, streamedText, true);
  }

  private processStreamLine(line: string): string | null {
    // Handle different stream formats: data, citations, tool_use, mcp_ui
    if (line.startsWith('data: ')) {
      const payload = line.substring(6);
      try {
        // Chunks are JSON-encoded partial tokens ("Let", " me", …): decode
        // and append as-is — adding newlines would split words across lines.
        const parsed = JSON.parse(payload);
        if (typeof parsed === 'string') return parsed;
      } catch {
        // Not JSON (older fixtures): fall through to raw-line handling
      }
      return `${payload}\n`;
    }

    if (line.startsWith('citations: ')) {
      // Sources grounding the AI answer — structured metadata, not content.
      try {
        const parsed = JSON.parse(line.substring(11));
        if (Array.isArray(parsed)) {
          const seen = new Set(this.citations.map((c) => c.url));
          for (const citation of parsed) {
            if (citation && citation.url && !seen.has(citation.url)) {
              seen.add(citation.url);
              this.citations.push({
                url: String(citation.url),
                title: String(citation.title || citation.url),
                type: citation.type ? String(citation.type) : undefined,
                snippet: citation.snippet
                  ? String(citation.snippet)
                  : undefined,
              });
            }
          }
        }
      } catch {
        // Malformed citations must never break the answer stream
      }
      return null;
    }

    // tool_use / mcp_ui events are agent telemetry, not answer content —
    // never inline them into the visible AI overview text.
    if (line.startsWith('tool_use: ') || line.startsWith('mcp_ui: ')) {
      return null;
    }

    return null;
  }

  private createResponse(
    content: string,
    streamedContent: string,
    isComplete: boolean
  ): AIPreviewResponse {
    return {
      content: this.sanitizeContent(content),
      streamedContent: this.sanitizeContent(streamedContent),
      contentType: 'data',
      isComplete,
      metadata: {
        responseTime: Date.now(),
        generatedAt: new Date(),
        sources: this.citations.length > 0 ? [...this.citations] : undefined,
      },
    };
  }

  private sanitizeContent(content: string): string {
    // Basic XSS prevention - remove potential script tags and dangerous content
    return content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  }

  private createError(error: any): AIPreviewError {
    if (error.name === 'AbortError') {
      return {
        type: 'timeout',
        message: 'Request was cancelled',
        isRetryable: true,
      };
    }

    if (error.message.includes('fetch')) {
      return {
        type: 'network',
        message: 'Network connection failed',
        details: error.message,
        isRetryable: true,
      };
    }

    if (error.message.includes('429')) {
      return {
        type: 'quota',
        message: 'Rate limit exceeded. Please try again later.',
        isRetryable: true,
        retryAfter: 60,
      };
    }

    return {
      type: 'parsing',
      message: 'Failed to process AI response',
      details: error.message,
      isRetryable: false,
    };
  }

  cancelCurrentRequest(): void {
    if (this.currentController) {
      this.currentController.abort();
      this.currentController = null;
    }
  }

  updateConfig(newConfig: Partial<AIStreamingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}
